import useSWR from 'swr';
import { fetchCities } from './data-source.ts';
import type { ILocationsItemProps } from '@ui/location/locations-list';
import { useAtom, useSetAtom } from 'jotai/index';
import { citiesAtom, cityAtom } from '@app/atoms/app/city.atom.ts';
import { navbarAtom } from '@app/atoms/app/navbar.atom.ts';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function useCities({ query }: { query: string }) {
  const navigate = useNavigate();
  const { data } = useSWR('/api/cities', fetchCities, {
    suspense: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const [selectedCity, setSelectedCity] = useAtom(cityAtom);
  const setNavbarCity = useSetAtom(navbarAtom);
  const setCities = useSetAtom(citiesAtom);

  const mapped = data
    ?.map<ILocationsItemProps | undefined>((locationItem) => {
      return {
        name: locationItem?.name?.toString(),
        value: locationItem?.id.toString(),
      };
    })
    .filter((i) => {
      if (i && query.length > 0 && i.name) {
        return i.name.toLowerCase().search(query.toLowerCase()) >= 0;
      } else {
        return true;
      }
    });

  const array = Array(10)
    .fill(undefined)
    .map(() => ({
      isLoading: true,
    }));

  function onCitySelect(stringId: string) {
    if (!data) return;
    const city = data.find(({ id }) => {
      return `${id}` === stringId;
    });

    if (city) {
      setSelectedCity({ id: city.id!, name: city.name });
      localStorage.setItem('selected-city-id', city.id.toString());
      setNavbarCity((p) => ({ ...p, place: city.name }));
      navigate('/');
    }
  }

  useEffect(() => {
    if (data) {
      setCities(data);

      const savedCityId = localStorage.getItem('selected-city-id');
      if (savedCityId) {
        const city = data.find(({ id }) => {
          return `${id}` === savedCityId;
        });

        if (city) {
          setSelectedCity({ id: city.id || 1, name: city.name || '' });
          setNavbarCity((p) => ({ ...p, place: city.name }));
        }
      }
    }
  }, [data]);

  return {
    data: mapped || array,
    onCitySelect,
    selectedCity: selectedCity.id.toString(),
    query,

    rawData: data,
  };
}
